import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/Common";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Puzzle, ShieldCheck, Code2, Copy } from "lucide-react";

export default function AdminPlugin() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") api.get("/admin/plugin/info").then((r) => setInfo(r.data)).catch(() => {});
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/plugin/download`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `myelite-favorites-${info?.version || "1.0.0"}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plugin downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };

  const shortcode = '[myelite_favorite id="sch-hec" type="school"]';

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-5 py-8">
        <PageHeader eyebrow="Administration" title="WordPress Plugin" subtitle="Download the MyElite Favorites plugin and add heart buttons to the Elite vitrine site." />

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white rounded-3xl border border-border shadow-card p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-orange/15 flex items-center justify-center"><Puzzle className="h-6 w-6 text-orange" /></div>
              <div>
                <h2 className="font-display font-extrabold italic text-navy text-lg">MyElite Favorites</h2>
                <p className="text-xs text-muted-foreground">Version {info?.version || "…"} · WordPress plugin</p>
              </div>
            </div>
            <ol className="space-y-3 text-sm text-navy list-decimal pl-5 mb-6">
              <li>Download the ZIP and upload it in WordPress → Plugins → Add New → Upload.</li>
              <li>Activate it, then open <b>Settings → MyElite Favorites</b>.</li>
              <li>Set the API base and app base URLs to this MyElite domain.</li>
              <li>Place a button with the shortcode below (id = catalog external_id).</li>
            </ol>
            <div className="flex items-center gap-2 bg-navy text-white rounded-xl px-4 py-3 font-mono text-sm">
              <Code2 className="h-4 w-4 text-orange shrink-0" />
              <span className="flex-1 truncate">{shortcode}</span>
              <button data-testid="copy-shortcode" onClick={() => { navigator.clipboard.writeText(shortcode); toast.success("Copied"); }} className="text-white/70 hover:text-white"><Copy className="h-4 w-4" /></button>
            </div>
            <Button data-testid="download-plugin-btn" onClick={download} disabled={busy} className="mt-6 h-12 rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase tracking-wide">
              <Download className="h-4 w-4 mr-2" /> {busy ? "Preparing…" : "Download plugin (.zip)"}
            </Button>
          </div>

          <div className="bg-navy text-white rounded-3xl p-7">
            <ShieldCheck className="h-8 w-8 text-orange mb-3" />
            <h3 className="font-display font-extrabold italic text-lg">Secure by design</h3>
            <ul className="mt-3 space-y-2 text-sm text-white/70">
              <li>• Bearer-token auth — no anonymous writes</li>
              <li>• Item data resolved server-side from the catalog</li>
              <li>• Per-user rate limiting & favorite caps</li>
              <li>• Login token returned only to whitelisted origins</li>
            </ul>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
